import { useState } from 'react';

import { ArticlePage } from '../../model/components/ArticlePage';
import { ArticlePostData } from '../../model/types/ArticlePostType';
import { PathContextProvider } from '../../PathContext';
import { ArticlePageInput } from './ArticlePageInput';
import { CrumpetCornerInput } from './CrumpetCornerInput';

import styles from './InteractiveArticlePostInput.module.scss';

export interface InteractiveArticleInputProps {
    value: ArticlePostData;
    onChange?: (value: ArticlePostData) => void;
}

export const InteractiveArticlePostInput = ({
    value,
    onChange,
}: InteractiveArticleInputProps) => {
    const [activePageIndex, setActivePageIndex] = useState(0);
    const pages =
        value.pages.length >= 2
            ? value.pages
            : [...value.pages, {}, {}].slice(0, 2);

    const onPageChange = (pageIndex: number, page: ArticlePage) => {
        const newPages = [...pages];
        newPages[pageIndex] = page;
        onChange?.({
            ...value,
            pages: newPages,
        });
    };

    const addPage = () => {
        if (pages.length < 10) {
            const newPages = [...pages];
            // Clear button and crumpet corner from previously last page
            // since only the last page should have buttons and only pages before another page should have crumpet corners
            if (newPages.length > 0) {
                const lastPageIndex = newPages.length - 1;
                newPages[lastPageIndex] = {
                    ...newPages[lastPageIndex],
                    button: undefined,
                    crumpetCorner: undefined,
                };
            }
            // Add new empty page
            newPages.push({});

            onChange?.({
                ...value,
                pages: newPages,
            });
        }
    };

    const removePage = (pageIndex: number) => {
        if (pages.length > 2) {
            const newPages = pages.filter((_, index) => index !== pageIndex);

            // Clear crumpet corner from new last page since it no longer has a next page
            if (newPages.length > 0) {
                const newLastPageIndex = newPages.length - 1;
                newPages[newLastPageIndex] = {
                    ...newPages[newLastPageIndex],
                    crumpetCorner: undefined,
                };
            }

            onChange?.({
                ...value,
                pages: newPages,
            });

            if (activePageIndex >= newPages.length) {
                setActivePageIndex(newPages.length - 1);
            }
        }
    };

    return (
        <div className={styles.container}>
            <PathContextProvider segment={'pages'}>
                <div className={styles.tabs}>
                    {pages.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`${styles.tab} ${
                                activePageIndex === index
                                    ? styles.activeTab
                                    : ''
                            }`}
                            onClick={() => setActivePageIndex(index)}
                        >
                            Page {index + 1}
                            <button
                                type="button"
                                className={styles.removePageButton}
                                disabled={pages.length <= 2}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removePage(index);
                                }}
                                title={
                                    pages.length <= 2
                                        ? 'Cannot remove - minimum 2 pages required'
                                        : `Remove Page ${index + 1}`
                                }
                            >
                                x
                            </button>
                        </button>
                    ))}
                    {pages.length < 10 && (
                        <button
                            type="button"
                            onClick={addPage}
                            disabled={pages.length >= 10}
                            className={styles.plusTab}
                            title="Add new page"
                        >
                            +
                        </button>
                    )}
                </div>

                <div className={styles.pageContent}>
                    <fieldset>
                        <legend>Page {activePageIndex + 1}</legend>

                        <PathContextProvider segment={activePageIndex}>
                            <ArticlePageInput
                                value={pages[activePageIndex] || {}}
                                onChange={(page) =>
                                    onPageChange(activePageIndex, page)
                                }
                                pageIndex={activePageIndex}
                                button={
                                    activePageIndex === pages.length - 1
                                        ? {
                                              enabled: true,
                                              allowedActions: [
                                                  'none',
                                                  'OpenLocation',
                                                  'OpenDistrict',
                                                  'OpenCharacterCreator',
                                                  'OpenOutfitDesigner',
                                                  'OpenCEX',
                                                  'OpenPortal',
                                                  'OpenInStore',
                                              ],
                                          }
                                        : undefined
                                }
                            />
                            {/* TODO: Move crumpet corner INTO the article page input  */}
                            <PathContextProvider segment={'crumpetCorner'}>
                                {pages[activePageIndex + 1] && (
                                    <fieldset>
                                        <legend>Crumpet Corner</legend>
                                        <CrumpetCornerInput
                                            value={
                                                pages[activePageIndex]
                                                    ?.crumpetCorner
                                            }
                                            onChange={(crumpetCorner) => {
                                                onPageChange(activePageIndex, {
                                                    ...pages[activePageIndex],
                                                    crumpetCorner,
                                                });
                                            }}
                                        />
                                    </fieldset>
                                )}
                            </PathContextProvider>
                        </PathContextProvider>
                    </fieldset>
                </div>
            </PathContextProvider>
        </div>
    );
};
